// Layer 4 (UI) — the panel row's left card (polish.P20, comp A `whereCard`):
// "Where the risk sits" — the heat map with its consequence lens on the left,
// the flagged risks on the right, one card split by a hairline.

import { cn } from '@/shared/cn';
import { DowntimeCard } from '../downtime/DowntimeCard';
import { RiskFlagList } from './RiskFlagList';
import { RiskHeatMap } from './RiskHeatMap';
import { RISK_CONSEQUENCE_MODES, type RiskConsequenceMode } from './Scene.transform';

/** The consequence lens (comp `.seg.mini`): the pressed option is ink-filled. */
function ConsequenceToggle({ mode, onChange }: { mode: RiskConsequenceMode; onChange: (mode: RiskConsequenceMode) => void }) {
  return (
    <span role="group" aria-label="Consequence" className="inline-flex rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-0.5">
      {RISK_CONSEQUENCE_MODES.map((option) => {
        const pressed = option.id === mode;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={pressed}
            data-consequence-mode={option.id}
            onClick={() => onChange(option.id)}
            className={cn(
              'whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]',
              pressed ? 'bg-ink-primary text-[var(--surface-2)]' : 'text-ink-secondary hover:text-ink-primary',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  );
}

export function WhereRiskSits({
  mode,
  onModeChange,
  selectedCell,
  onSelectCell,
}: {
  mode: RiskConsequenceMode;
  onModeChange: (mode: RiskConsequenceMode) => void;
  selectedCell: string | null;
  onSelectCell: (key: string | null) => void;
}) {
  return (
    <DowntimeCard
      title="Where the risk sits"
      actions={<ConsequenceToggle mode={mode} onChange={onModeChange} />}
      className="h-full"
    >
      <div className="grid grid-cols-1 gap-y-4 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <div className="md:pr-3.5">
          <RiskHeatMap mode={mode} selectedCell={selectedCell} onSelectCell={onSelectCell} />
        </div>
        <div className="min-w-0 md:border-l md:border-[var(--hairline)] md:pl-3.5">
          <RiskFlagList />
        </div>
      </div>
    </DowntimeCard>
  );
}
